import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { jobApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { TextArea } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { toast } from '@/store/toast'

interface Props {
  open: boolean
  onClose: () => void
  onAdded?: (jobId: string) => void
}

/** The "Add your own link" modal: paste a URL, optionally fetch the JD. */
export function AddJobModal({ open, onClose, onAdded }: Props) {
  const queryClient = useQueryClient()
  const [url, setUrl] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setUrl('')
    setDescription('')
    setError(null)
  }

  const fetchJd = useMutation({
    mutationFn: () => jobApi.fetchDescription(url.trim()),
    onSuccess: (result) => {
      if (result.description) {
        setDescription(result.description)
        toast.success('Description fetched', result.title ?? undefined)
      } else {
        // Many boards block server-side fetches; pasting is the fallback.
        toast.info('Could not read that page', 'Paste the job description below instead.')
      }
    },
    onError: (caught: ApiError) => setError(caught.message),
  })

  const add = useMutation({
    mutationFn: () =>
      jobApi.addByUrl({
        jobUrl: url.trim(),
        jobDescription: description.trim() || undefined,
      }),
    onSuccess: (match) => {
      queryClient.invalidateQueries({ queryKey: ['top-matches'] })
      queryClient.invalidateQueries({ queryKey: ['recommendations'] })
      toast.success('Job added', `${match.job.title} — ${match.matchScore}% match`)
      onAdded?.(match.job.id)
      reset()
      onClose()
    },
    onError: (caught: ApiError) => setError(caught.message),
  })

  const hasUrl = url.trim().length > 8
  const canSubmit = hasUrl && !add.isPending

  return (
    <Modal
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title="Add your own link"
      description="Paste the page with the Apply button. We score it against your profile and tailor your resume before applying."
      size="md"
      footer={
        <Button fullWidth onClick={() => add.mutate()} disabled={!canSubmit} loading={add.isPending}>
          Apply with Optimized Resume →
        </Button>
      }
    >
      <div className="space-y-4 pb-2">
        <div>
          <label htmlFor="job-url" className="label-eyebrow mb-2 block">
            Job URL
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              id="job-url"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value)
                setError(null)
              }}
              placeholder="https://boards.greenhouse.io/company/jobs/123"
              inputMode="url"
              autoComplete="off"
              className="field-box h-11 min-w-0 flex-1"
            />
            <Button
              variant="secondary"
              onClick={() => fetchJd.mutate()}
              disabled={!hasUrl || fetchJd.isPending}
              loading={fetchJd.isPending}
              className="shrink-0"
            >
              Fetch JD
            </Button>
          </div>
        </div>

        <TextArea
          label="Job description"
          appearance="box"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Fetch it from the link above, or paste the job description here."
          rows={6}
          hint="Optional. With a description we can tailor your resume to the role."
        />

        {error && (
          <p role="alert" className="rounded-field border border-danger/20 bg-danger/[0.06] px-3.5 py-2.5 text-[13px] text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  )
}
